import { useSafeQuery } from '@gen2/hooks';
import { useMutation, UseQueryOptions } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import {
  addContactGroupsToInvite,
  addContactsToInvite,
  deleteContactFromInvite,
  draftInvite,
  editDraftInvite,
  getInvite,
  mergeTemplates,
  sendInvite,
  TInvite,
  updateInviteAssignee,
  updateInviteTeam,
} from './api';

export enum InvitesKeys {
  getInvite = 'getInvite',
  editDraftInvite = 'editDraftInvite',
  createDraftInvite = 'createDraftInvite',
  mergeTemplates = 'mergeTemplates',
  updateInviteAssignee = 'updateInviteAssignee',
  updateInviteTeam = 'updateInviteTeam',
}

export const useDraftInviteMutation = () =>
  useMutation({
    mutationKey: [InvitesKeys.createDraftInvite],
    mutationFn: draftInvite,
  });

export const useEditDraftInviteMutation = () =>
  useMutation({
    mutationKey: [InvitesKeys.editDraftInvite],
    mutationFn: editDraftInvite,
  });

export const useAddContactsToInviteMutation = () =>
  useMutation({ mutationFn: addContactsToInvite });

export const useAddContactGroupsToInviteMutation = () =>
  useMutation({ mutationFn: addContactGroupsToInvite });

export const useRemoveContactFromInviteMutation = () =>
  useMutation({ mutationFn: deleteContactFromInvite });

export const useInviteQuery = (
  id: string,
  options?: UseQueryOptions<TInvite>,
) => {
  return useSafeQuery<TInvite>(
    [InvitesKeys.getInvite, id],
    async () => {
      const res = await getInvite(id);

      return res?.data.data.invite;
    },
    {
      ...options,
      retry: false,
      onError: (err) => {
        const error = err as AxiosResponse;

        if (error.status === 404) {
          globalThis.location.href = '/404';
        }
      },
    },
  );
};

export const useSendInviteMutation = () =>
  useMutation({ mutationFn: sendInvite });

export const useMergeTemplatesMutation = () =>
  useMutation({
    mutationFn: mergeTemplates,
    mutationKey: [InvitesKeys.mergeTemplates],
  });

export const useUpdateInviteAssigneeMutation = () =>
  useMutation({
    mutationFn: updateInviteAssignee,
    mutationKey: [InvitesKeys.updateInviteAssignee],
  });

export const useUpdateInviteTeamMutation = () =>
  useMutation({
    mutationFn: updateInviteTeam,
    mutationKey: [InvitesKeys.updateInviteTeam],
  });
